import type { CustomerDataWithStatus } from '@packages/utilities';
import type { FormValues } from '../types';
import { InputIDs } from '../types';

/**
 * Maps customer's gender to a suggested preselection of salutation
 */
const genderSalutationMap = {
  m: 'male',
  f: 'female',
  d: 'notSpecified',
};

/**
 * Converts customer's birthday to a value for a date input
 * @param birthday String in format dd.mm.yyyy
 * @returns Date
 */
const birthdayToDate = (birthday: string) => {
  try {
    const [day, month, year] = birthday.split('.');

    // convert to Date to make sure the string was valid
    // get string for input
    return new Date(`${year}-${month}-${day}`);
  } catch {
    /* istanbul ignore next */
    return undefined;
  }
};

/**
 * Prefills fields with customer data
 *
 * @param fieldsToPrefill Fields you wish to fill
 * @param customer
 * @param formValues
 * @param callback Function that is called when a value from customer was found and the formfield is empty
 */
export const prefillForm = (
  fieldsToPrefill: InputIDs[],
  customer: CustomerDataWithStatus | undefined,
  formValues: FormValues,
  callback: (customerValue: string | Date, id: InputIDs) => void,
) => {
  if (!customer) {
    return;
  }

  /**
   * Object mapping the inputId to the customer value
   */
  const fieldsCustomerMap = {
    [`${InputIDs.MESSAGE}`]: undefined,
    [`${InputIDs.FREQUENCY}`]: undefined,
    [`${InputIDs.PAUSE}`]: undefined,
    [`${InputIDs.SUBJECT}`]: undefined,
    [`${InputIDs.FIRSTNAME}`]: customer?.firstName,
    [`${InputIDs.LASTNAME}`]: customer?.lastName,
    [`${InputIDs.STREET}`]: customer?.billingAddress?.street,
    [`${InputIDs.HOUSENUMBER}`]: customer?.billingAddress?.houseNumber,
    [`${InputIDs.CITY}`]: customer?.billingAddress?.city,
    [`${InputIDs.POSTALCODE}`]: customer?.billingAddress?.zipCode,
    [`${InputIDs.EMAIL}`]: customer?.email,
    [`${InputIDs.CUSTOMERID}`]: customer?.businessPartnerNo,
    // "find" first element in array. Can't use phoneNumbers[0, as array might be undefined
    [`${InputIDs.PHONENUMBER}`]: customer?.phoneNumbers?.find(Boolean)?.number,
    [`${InputIDs.BIRTHDAY}`]: customer?.birthDay && birthdayToDate(customer.birthDay),
    [`${InputIDs.SALUTATION}`]: customer?.gender && genderSalutationMap[customer.gender],
  };

  fieldsToPrefill.forEach((id) => {
    const customerValue = fieldsCustomerMap[id];

    // call callback if the value exists and the field is empty
    if (customerValue && (!formValues[id] || formValues[id] === 'notSpecified')) {
      callback(customerValue, id);
    }
  });
};
